import {
  autoUpdate,
  offset,
  shift,
  useClientPoint,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { Point2d } from '../../../../../../viewer/utilities/math-helpers';
import { Icon } from '../../../../../components/icon/icon';
import { useSubscribe } from '../../../../../hooks/use-subscribe';

export const ImageInspectionHover = () => {
  const [imageData, setImageData] = useState<
    | {
        name: string;
        position: Point2d;
        url: string;
      }
    | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const onImageHover = useCallback(
    (message: { data?: { url: string; name: string; position: Point2d } }) => {
      if (!message.data) {
        setIsLoading(false);
        setImageData(undefined);
        return;
      }

      // When new data arrives with a new url, immediately set loading state and clear old image
      if (message.data.url !== imageData?.url) {
        setIsLoading(true);
        setImageData(message.data);
      }
    },
    [imageData?.url],
  );

  useSubscribe(SubscriptionTopic.OnInspectionToolImageHover, onImageHover);

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset({
        mainAxis: 10,
        crossAxis: 10,
      }),
      shift({
        padding: 5,
        crossAxis: true,
      }),
    ],
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
  });

  const clientPoint = useClientPoint(
    context,
    imageData?.position ? { x: imageData.position.x, y: imageData.position.y } : undefined,
  );

  const { getFloatingProps } = useInteractions([clientPoint]);

  if (!imageData) {
    return null;
  }

  return (
    <HoverContainer ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
      <ImageContainer>
        {isLoading && (
          <SpinnerContainer>
            <Icon color="#fff" icon="spinner" spin={true} />
          </SpinnerContainer>
        )}
        <ImagePreview
          alt={imageData.name}
          key={imageData.url}
          src={imageData.url}
          style={{ display: isLoading ? 'none' : 'block' }}
          onError={(e) => {
            // Hide image on error
            setIsLoading(false);
            e.currentTarget.style.display = 'none';
          }}
          onLoad={() => {
            setIsLoading(false);
          }}
        />
      </ImageContainer>
      <ImageName>{imageData.name}</ImageName>
    </HoverContainer>
  );
};

const HoverContainer = styled.div`
  position: fixed;
  width: 120px;
  background-color: ${(props) => props.theme.color.gray.darkest};
  border-radius: 0.5em;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 20000;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinnerContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: auto;
  border-radius: 0.25em;
  object-fit: cover;
  display: block;
`;

const ImageName = styled.div`
  font-size: 11px;
  color: #fff;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
`;
